/**
 * Position Requests tab — review & provision staged "create positions" requests.
 *
 * Lists NEW_POSITION change requests; a single reviewer approves (which provisions
 * to F&O + upserts locally) or rejects. Partial provisioning stays PENDING for retry
 * ("Provision rest" only creates the outstanding count).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Loader2, RefreshCw, CheckCircle, XCircle, Clock, Layers, Building2, ChevronDown, ChevronRight,
  Wallet, Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/hooks/useConfirm';
import {
  organizationService, ChangeRequest, budgetService, FundingResolution, RequestFunding,
} from '@/services/api/organizationService';
import { positionRequestService } from '@/services/api/positionRequestService';
import type { ExistingSeat, ExistingSeatsResult } from '@/services/api/positionRequestService';
import ExistingSeatsPanel from '@/components/admin/lifecycle/ExistingSeatsPanel';
import BusinessCaseView from '@/components/admin/lifecycle/BusinessCaseView';
import {
  appointmentLabel, describeOrigin, dimsLabel, matchChipLabel,
} from '@/components/admin/lifecycle/positionRequestStatus';

// One entry per (job, dims, type) group; a single-shape request is one group.
const requestGroups = (pc: any): any[] =>
  (Array.isArray(pc?.position_groups) && pc.position_groups.length ? pc.position_groups : [pc || {}]);

const zarK = (v: number) => (Math.abs(v) >= 1e6 ? `R${(v / 1e6).toFixed(1)}m` : `R${Math.round(v / 1e3)}k`);
// Backend 409 detail may be an object ({error, detail}) — always render a string.
const errText = (e: any, fallback: string) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : (d?.detail || fallback);
};

// A dimension-update request corrects existing seats; everything else creates new ones.
const isDimUpdate = (r: ChangeRequest) =>
  (r as any).request_type === 'update_position_dimensions'
  || Array.isArray((r.proposed_changes as any)?.position_updates);

// One entry per person, keyed on e-mail where we have it (names can repeat).
const requesterKey = (r: ChangeRequest) =>
  r.requested_by_email?.toLowerCase() || `user:${r.requested_by_user_id}`;
const requesterLabel = (r: ChangeRequest) =>
  r.requested_by_name || r.requested_by_email || `User ${r.requested_by_user_id}`;

// Every campus a request touches: the single ask, each batch group, or the campus
// each seat is being corrected TO (the server stores updates as {old_dims, new_dims}).
const requestCampuses = (r: ChangeRequest): string[] => {
  const pc: any = r.proposed_changes || {};
  const out = new Set<string>();
  if (pc.campus) out.add(pc.campus);
  if (Array.isArray(pc.position_groups)) pc.position_groups.forEach((g: any) => g?.campus && out.add(g.campus));
  if (Array.isArray(pc.position_updates)) pc.position_updates.forEach((u: any) => u?.new_dims?.campus && out.add(u.new_dims.campus));
  return Array.from(out);
};

// Funding only applies to creates; a dimension update has nothing to fund.
// The server calls a request 'line' as soon as ANY seat has money, so a request
// with seats still unresolved stays unresolved here: that is the work left, and
// the server keeps budget_approved false until every seat is resolved.
type FundingState = 'funded' | 'unbudgeted' | 'unresolved' | null;
const fundingState = (r: ChangeRequest): FundingState => {
  if (isDimUpdate(r)) return null;
  const f: any = (r.proposed_changes as any)?.funding;
  if (f?.mode !== 'line' && f?.mode !== 'unbudgeted') return 'unresolved';
  // Per-seat funding counts seats; the older single-line shape has no counts.
  const want = Number(f.positions || 0);
  const resolved = f.resolved_positions ?? (Array.isArray(f.lines) ? f.lines.length : null);
  if (want && resolved !== null && Number(resolved) < want) return 'unresolved';
  return f.mode === 'line' ? 'funded' : 'unbudgeted';
};

// Everything a reviewer might type to find a request.
const searchText = (r: ChangeRequest) => {
  const pc: any = r.proposed_changes || {};
  const groups: any[] = Array.isArray(pc.position_groups) ? pc.position_groups : [];
  return [
    r.title, r.request_number, r.requested_by_name, r.requested_by_email,
    pc.job_id, pc.title, ...groups.flatMap(g => [g?.job_id, g?.title]),
  ].filter(Boolean).join(' ').toLowerCase();
};

const STATUS_META: Record<string, { label: string; tone: string; icon: React.ReactNode }> = {
  pending_approval: { label: 'Pending review', tone: 'text-amber-600 border-amber-200 bg-amber-50', icon: <Clock className="h-3 w-3" /> },
  implemented: { label: 'Provisioned', tone: 'text-emerald-700 border-emerald-200 bg-emerald-50', icon: <CheckCircle className="h-3 w-3" /> },
  approved: { label: 'Provisioning…', tone: 'text-blue-600 border-blue-200 bg-blue-50', icon: <Loader2 className="h-3 w-3 animate-spin" /> },
  rejected: { label: 'Rejected', tone: 'text-red-600 border-red-200 bg-red-50', icon: <XCircle className="h-3 w-3" /> },
  cancelled: { label: 'Withdrawn', tone: 'text-gray-500 border-gray-200 bg-gray-50', icon: <XCircle className="h-3 w-3" /> },
};

const PositionRequestsTab: React.FC = () => {
  const { confirm: showConfirm } = useConfirm();
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  // List filters — applied client-side on top of the pending/all toggle.
  const [search, setSearch] = useState('');
  const [requesterFilter, setRequesterFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'new' | 'dimensions'>('all');
  const [campusFilter, setCampusFilter] = useState('all');
  const [fundingFilter, setFundingFilter] = useState<'all' | 'funded' | 'unbudgeted' | 'unresolved'>('all');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Requests whose group breakdown is expanded (collapsed by default for big batches).
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // Funding-resolution dialog (the budget↔requests link).
  const [fundingFor, setFundingFor] = useState<ChangeRequest | null>(null);
  // One resolution per GROUP — a two-job ask has two different candidate
  // lists, and resolving only group 0 is what made the dialog unable to fund
  // the second job at all.
  const [resolutions, setResolutions] = useState<Record<number, FundingResolution>>({});
  const [resolution, setResolution] = useState<FundingResolution | null>(null);
  const [resolving, setResolving] = useState(false);
  // seat ordinal -> item_id. A request for N positions needs N lines.
  const [picks, setPicks] = useState<Record<number, number>>({});
  // Which group the dialog is scoped to; null = the whole ask. Resolving one
  // job at a time is the point — a 3-job batch in one dialog is unreadable.
  const [fundingGroup, setFundingGroup] = useState<number | null>(null);
  const [unbudgetedReason, setUnbudgetedReason] = useState('');
  // Cross-type attach override (the CHRO's rule: deliberate + justified).
  const [fundingOverride, setFundingOverride] = useState(false);
  const [fundingBusy, setFundingBusy] = useState(false);
  // Inline reporting-line repair on a reuse candidate (F&O PositionHierarchies
  // 'Line' edge — position-anchored, so it works on a vacant seat).
  const [rtBusy, setRtBusy] = useState<string | null>(null);
  // Vacant positions that already match each pending request's groups, keyed
  // `${request id}:${group index}` — funded or not, unlike the funding dialog.
  const [seatChecks, setSeatChecks] = useState<Record<string, ExistingSeatsResult>>({});
  const [seatsOpen, setSeatsOpen] = useState<Set<number>>(new Set());
  const seatCheckKey = useRef('');
  // Rejecting says why — the person who raised it reads the reason in HR Ops.
  const [rejecting, setRejecting] = useState<ChangeRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [useInstead, setUseInstead] = useState('');

  const fixReportingLine = async (seatCode: string, parentCode: string) => {
    setRtBusy(seatCode);
    try {
      const res = await organizationService.setPositionReportsTo(seatCode, parentCode);
      if (res.ok) {
        setMsg({ type: 'success', text: `${seatCode} now reports to ${parentCode} (written to F&O).` });
        if (fundingFor) await openFunding(fundingFor);   // re-resolve so the flag clears
      } else {
        setMsg({ type: 'error', text: res.error || `Could not repoint ${seatCode} in F&O.` });
      }
    } catch (e: any) {
      setMsg({ type: 'error', text: errText(e, `Could not repoint ${seatCode}.`) });
    } finally {
      setRtBusy(null);
    }
  };

  // Campus · department · faculty, as F&O spells them.  NA is shown rather than
  // hidden: "faculty not set" is itself something the reviewer needs to see.
  const dimLabel = (c: { campus?: string | null; department?: string | null; faculty?: string | null }) =>
    [c.campus, c.department, c.faculty].filter(Boolean).join('·');
  // Why a candidate is not a match, said plainly.  "other dims" on a row with
  // the SAME campus and department hid that its money is another faculty's.
  // match_tier is absent from an older resolver, so fall back to dims_match.
  const dimTag = (c: { dims_match: boolean; match_tier?: number }): string | null => {
    if (c.dims_match) return null;
    if (c.match_tier === 2) return 'other faculty';
    if (c.match_tier === 1) return 'faculty not set';
    return 'other dims';
  };

  // Mirrors _ask_slots in budget_workbench.py: one entry per position the ask
  // creates, in the order the provisioner returns them.
  const askSlots = (pc: any): Array<{ ordinal: number; groupIndex: number; group: any }> => {
    const groups = Array.isArray(pc?.position_groups) ? pc.position_groups : [];
    const out: Array<{ ordinal: number; groupIndex: number; group: any }> = [];
    if (groups.length) {
      groups.forEach((g: any, gi: number) => {
        for (let k = 0; k < Math.max(1, Number(g?.count) || 1); k += 1) {
          out.push({ ordinal: out.length, groupIndex: gi, group: g });
        }
      });
      return out;
    }
    const n = Math.max(1, Number(pc?.count) || 1);
    for (let i = 0; i < n; i += 1) out.push({ ordinal: i, groupIndex: 0, group: pc || {} });
    return out;
  };

  // Everything the dialog's title and footer need about the CURRENT scope.
  const fundingSlots = fundingFor ? askSlots((fundingFor.proposed_changes as any) || {}) : [];
  const fundingScope = fundingSlots
    .filter(sl => fundingGroup == null || sl.groupIndex === fundingGroup)
    .map(sl => sl.ordinal);
  const fundingPickCount = Object.keys(picks)
    .filter(o => fundingScope.includes(Number(o))).length;
  // Already-resolved scope stays saveable so the save can CLEAR it — otherwise
  // an attached line could be revised but never removed.
  const fundingWasResolved = (() => {
    const rows = (fundingFor?.proposed_changes as any)?.funding?.lines;
    return Array.isArray(rows) && rows.some((l: any) => fundingScope.includes(Number(l.seat_ordinal)));
  })();
  const fundingGroupLabel = (() => {
    if (fundingGroup == null) return '';
    const g = fundingSlots.find(sl => sl.groupIndex === fundingGroup)?.group;
    if (!g) return '';
    return [g.title || g.job_id, [g.campus, g.department, g.faculty].filter(Boolean).join('/')]
      .filter(Boolean).join(' · ');
  })();

  const openFunding = async (req: ChangeRequest, groupIndex: number | null = null) => {
    const pc: any = req.proposed_changes || {};
    const inScope = (o: number) => groupIndex == null
      || askSlots(pc).some(sl => sl.ordinal === o && sl.groupIndex === groupIndex);
    // Prefill from what is already resolved. Opening blank was how a reopened
    // dialog silently DROPPED the lines already attached on the next save.
    const stored: any[] = Array.isArray(pc.funding?.lines) ? pc.funding.lines : [];
    const seeded: Record<number, number> = {};
    stored.forEach(l => {
      const o = Number(l.seat_ordinal);
      if (inScope(o) && l.item_id && l.kind !== 'unbudgeted') seeded[o] = Number(l.item_id);
    });
    const seededReason = stored.find(l => inScope(Number(l.seat_ordinal))
                                       && l.kind === 'unbudgeted')?.reason || '';
    setFundingFor(req);
    setFundingGroup(groupIndex);
    setPicks(seeded);
    setUnbudgetedReason(seededReason);
    setFundingOverride(stored.some(l => inScope(Number(l.seat_ordinal)) && l.type_override));
    setResolution(null);
    setResolutions({});
    setResolving(true);
    try {
      const slots = askSlots(pc);
      const groupIdx = Array.from(new Set(slots.map(sl => sl.groupIndex)))
        .filter(gi => groupIndex == null || gi === groupIndex);
      const got: Record<number, FundingResolution> = {};
      await Promise.all(groupIdx.map(async gi => {
        const g = slots.find(sl => sl.groupIndex === gi)?.group || {};
        got[gi] = await budgetService.resolveBudgetFunding({
          title: g.title || pc.title || req.title,
          campus: g.campus || pc.campus || undefined,
          department: g.department || pc.department || undefined,
          // For academic roles the faculty is whose budget it is — PS·ACATEA
          // lines belong to IT, Commerce, Humanities and others alike.
          faculty: g.faculty || pc.faculty || undefined,
          // Budget is per position type — the resolver only offers matching seats/lines.
          position_type_id: g.position_type_id || pc.position_type_id || undefined,
          // Reusing a seat inherits ITS reporting line — flag any that differ.
          reports_to_position_id: g.reports_to_position_id || pc.reports_to_position_id || undefined,
        });
      }));
      setResolutions(got);
      setResolution(got[groupIdx[0]] || null);
    } catch {
      setResolution({ year: 2026, recommendation: 'unbudgeted', seats: [], lines: [] });
    } finally {
      setResolving(false);
    }
  };

  const saveFunding = async () => {
    if (!fundingFor) return;
    setFundingBusy(true);
    try {
      const allSlots = askSlots((fundingFor.proposed_changes as any) || {});
      // Say exactly which positions this save governs. Everything outside the
      // scope is carried forward server-side — that is what makes resolving job
      // 2 safe when job 1 already has money on it.
      const scope = allSlots
        .filter(sl => fundingGroup == null || sl.groupIndex === fundingGroup)
        .map(sl => sl.ordinal);
      const scoped = new Set(scope);
      const items = Object.entries(picks)
        .map(([ord, id]) => ({ item_id: id, seat_ordinal: Number(ord) }))
        .filter(x => scoped.has(x.seat_ordinal))
        .sort((a, b) => a.seat_ordinal - b.seat_ordinal);
      if (items.length) {
        await budgetService.setRequestFunding(fundingFor.id, {
          mode: 'line', items, scope_ordinals: scope,
          ...(fundingOverride ? { override_type_check: true } : {}),
        });
      } else if (unbudgetedReason.trim().length >= 5) {
        await budgetService.setRequestFunding(fundingFor.id, {
          mode: 'unbudgeted', reason: unbudgetedReason, scope_ordinals: scope });
      } else {
        await budgetService.setRequestFunding(fundingFor.id,
          { mode: 'clear', scope_ordinals: scope });
      }
      const n = items.length;
      const total = scope.length;
      setMsg({ type: 'success', text: n
        ? `Funding resolved — ${n} of ${total} position(s) funded.`
        : (unbudgetedReason.trim().length >= 5
          ? `Unbudgeted sign-off recorded for ${total} position(s).`
          : `Funding cleared for ${total} position(s).`) });
      setFundingFor(null);
      await load();
    } catch (e: any) {
      setMsg({ type: 'error', text: errText(e, 'Failed to attach funding.') });
    } finally {
      setFundingBusy(false);
    }
  };

  // Matching vacant positions for every pending new-position request, in
  // one call. Only re-asked when the set of pending requests changes: the
  // list refreshes every few seconds while something provisions.
  const loadSeatChecks = useCallback(async (list: ChangeRequest[]) => {
    const asks: any[] = [];
    for (const r of list) {
      const pc: any = r.proposed_changes || {};
      if (r.status !== 'pending_approval' || !(pc.job_id || Array.isArray(pc.position_groups))) continue;
      const own: string[] = pc.provision_result?.position_ids || [];
      requestGroups(pc).forEach((g: any, gi: number) => asks.push({
        key: `${r.id}:${gi}`,
        job_id: g.job_id || null,
        title: g.title || g.title_id || g.job_id || null,
        position_type_id: g.position_type_id || null,
        campus: g.campus || null,
        department: g.department || null,
        faculty: g.faculty || null,
        reports_to_position_id: g.reports_to_position_id || pc.reports_to_position_id || null,
        start_date: (g.start_date || pc.start_date || '').slice(0, 10) || null,
        exclude: own,
      }));
    }
    const key = asks.map(a => a.key).sort().join(',');
    if (key === seatCheckKey.current) return;
    seatCheckKey.current = key;
    if (!asks.length) { setSeatChecks({}); return; }
    try {
      const out: Record<string, ExistingSeatsResult> = {};
      for (let i = 0; i < asks.length; i += 60) {   // the endpoint takes 60 asks a call
        const res = await positionRequestService.existingSeats(asks.slice(i, i + 60));
        Object.assign(out, res.results || {});
      }
      setSeatChecks(out);
    } catch (e) {
      // Advisory only — the requests still load and can still be decided.
      console.error('Existing-position check failed', e);
      seatCheckKey.current = '';
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await organizationService.listChangeRequests({
        request_type: ['new_position', 'update_position_dimensions'],
        limit: 200,
      });
      setRequests(data);
      void loadSeatChecks(data);
    } catch (e) {
      console.error('Failed to load position requests', e);
    } finally {
      setLoading(false);
    }
  }, [loadSeatChecks]);

  // The vacant positions matching one request, closest first.
  const seatsFor = (req: ChangeRequest): ExistingSeat[] => requestGroups(req.proposed_changes)
    .flatMap((_: any, gi: number) => seatChecks[`${req.id}:${gi}`]?.candidates || []);

  const openReject = (req: ChangeRequest) => {
    setRejecting(req);
    setRejectReason('');
    setUseInstead('');
  };

  const confirmReject = async () => {
    if (!rejecting) return;
    setActing(rejecting.id);
    setMsg(null);
    try {
      await organizationService.approveChangeRequest(rejecting.id, {
        action: 'reject',
        comments: rejectReason.trim(),
        ...(useInstead ? { use_instead_position_id: useInstead } : {}),
      });
      setMsg({ type: 'success', text: `Request ${rejecting.request_number} rejected — the person who raised it sees your reason in HR Ops.` });
      setRejecting(null);
      await load();
    } catch (e: any) {
      setMsg({ type: 'error', text: errText(e, 'Failed to reject.') });
    } finally {
      setActing(null);
    }
  };

  useEffect(() => { load(); }, [load]);

  // A request is "in flight" while its background F&O fan-out runs (status approved).
  const inFlight = requests.some(r => r.status === 'approved');
  // Auto-refresh the list while anything is provisioning — the server-side job isn't
  // bound to this tab, so we just poll until it settles (no arbitrary 10-min cutoff).
  useEffect(() => {
    if (!inFlight) return;
    const t = setInterval(() => { load(); }, 5000);
    return () => clearInterval(t);
  }, [inFlight, load]);
  // Surface a banner when provisioning settles (done, or stopped partway for retry).
  const prevInFlight = useRef(false);
  useEffect(() => {
    if (prevInFlight.current && !inFlight) {
      const stuck = requests.find(r => r.status === 'pending_approval'
        && (((r.proposed_changes as any)?.provision_result?.failed ?? 0) > 0));
      const why = (stuck?.proposed_changes as any)?.provision_error;
      setMsg(stuck
        ? { type: 'error', text: why
            ? `Provisioning stopped partway — ${why}. Fix the cause on that request, then "Provision rest".`
            : 'Provisioning stopped partway — some positions remain. Use "Provision rest" on that request to resume.' }
        : { type: 'success', text: 'Provisioning finished — see the statuses below.' });
    }
    prevInFlight.current = inFlight;
  }, [inFlight, requests]);

  const act = async (req: ChangeRequest, action: 'approve' | 'reject') => {
    const pc: any = req.proposed_changes || {};
    const groups: any[] | null = Array.isArray(pc.position_groups) ? pc.position_groups : null;
    const isDim = (req as any).request_type === 'update_position_dimensions' || Array.isArray(pc.position_updates);
    const total = isDim ? (pc.position_updates?.length || 0)
      : groups ? groups.reduce((s: number, g: any) => s + (g.count || 1), 0)
      : (pc.count || 1);
    const confirmed = await showConfirm(
      action === 'approve'
        ? (isDim
            ? `Update dimensions on ${total} position(s) in F&O now? Each keeps its own dimensions.`
            : groups
              ? `Provision ${total} position(s) across ${groups.length} group(s) to F&O now? This creates real positions in F&O.`
              : `Provision ${total} position(s) for "${pc.title ? `${pc.title} (${pc.job_id})` : (pc.job_id || req.title)}" to F&O now? This creates real positions in F&O.`)
        : 'Reject this request?',
      action === 'approve' ? (isDim ? 'Approve & update' : 'Approve & provision') : 'Reject request',
      { confirmText: action === 'approve' ? (isDim ? 'Update' : 'Provision') : 'Reject', type: action === 'approve' ? 'warning' : 'info' },
    );
    if (!confirmed) return;
    setActing(req.id);
    setMsg(null);
    try {
      await organizationService.approveChangeRequest(req.id, { action });
      if (action === 'reject') {
        setMsg({ type: 'success', text: 'Request rejected.' });
        await load();
        return;
      }
      // Approve runs the F&O fan-out in a SERVER-SIDE background job — it isn't bound to
      // this tab. We don't poll it to death here; the list auto-refreshes while anything
      // is provisioning (and surfaces a banner when it settles), so a slow batch never
      // hits an arbitrary cutoff and the user can leave the tab.
      setMsg({ type: 'success', text: `Provisioning ${total} ${isDim ? 'update(s)' : 'position(s)'} in the background — this list updates automatically; you can leave this tab.` });
      await load();   // reflect APPROVED / Provisioning… immediately, which starts the auto-refresh
    } catch (e: any) {
      // Approval refused because no funding is resolved -> open the funding
      // dialog right there instead of a dead-end error.
      if (e?.response?.data?.detail?.error === 'funding_required') {
        setMsg({ type: 'error', text: 'Funding must be resolved before approval — pick a budget line or sign off unbudgeted.' });
        await openFunding(req);
      } else {
        setMsg({ type: 'error', text: errText(e, `Failed to ${action}.`) });
      }
    } finally {
      setActing(null);
    }
  };

  // Client-side filters — robust regardless of array query-param serialisation:
  // F&O provisioning requests = single creates (carry job_id), BATCH creates (carry
  // position_groups) OR dimension updates (carry position_updates). Unless "Show all",
  // show pending + in-flight (approved).
  const isProvisioningReq = (r: ChangeRequest) => {
    const pc: any = r.proposed_changes || {};
    return !!pc.job_id || Array.isArray(pc.position_groups)
      || (r as any).request_type === 'update_position_dimensions' || Array.isArray(pc.position_updates);
  };
  const provisioning = requests.filter(isProvisioningReq);
  const inScope = provisioning
    .filter(r => showAll || r.status === 'pending_approval' || r.status === 'approved');

  // Dropdown options come from every loaded request, so they don't shift when
  // "Show all" is toggled.
  const requesterOptions = Array.from(
    new Map(provisioning.map(r => [requesterKey(r), requesterLabel(r)])).entries(),
  ).sort((a, b) => a[1].localeCompare(b[1]));
  const campusOptions = Array.from(new Set(provisioning.flatMap(requestCampuses))).sort();

  const term = search.trim().toLowerCase();
  const visible = inScope.filter(r =>
    (!term || searchText(r).includes(term))
    && (requesterFilter === 'all' || requesterKey(r) === requesterFilter)
    && (typeFilter === 'all' || (typeFilter === 'dimensions') === isDimUpdate(r))
    && (campusFilter === 'all' || requestCampuses(r).includes(campusFilter))
    && (fundingFilter === 'all' || fundingState(r) === fundingFilter));

  const activeFilters = [
    term, requesterFilter !== 'all', typeFilter !== 'all', campusFilter !== 'all', fundingFilter !== 'all',
  ].filter(Boolean).length;
  const clearFilters = () => {
    setSearch('');
    setRequesterFilter('all');
    setTypeFilter('all');
    setCampusFilter('all');
    setFundingFilter('all');
  };

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search title, request no., job ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={requesterFilter} onValueChange={setRequesterFilter}>
          <SelectTrigger className="w-[190px]" aria-label="Requested by">
            <SelectValue placeholder="Requested by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Requesters</SelectItem>
            {requesterOptions.map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={v => setTypeFilter(v as typeof typeFilter)}>
          <SelectTrigger className="w-[170px]" aria-label="Request type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="new">New positions</SelectItem>
            <SelectItem value="dimensions">Dimension updates</SelectItem>
          </SelectContent>
        </Select>

        <Select value={campusFilter} onValueChange={setCampusFilter}>
          <SelectTrigger className="w-[140px]" aria-label="Campus">
            <SelectValue placeholder="Campus" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Campuses</SelectItem>
            {campusOptions.map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={fundingFilter} onValueChange={v => setFundingFilter(v as typeof fundingFilter)}>
          <SelectTrigger className="w-[150px]" aria-label="Funding">
            <SelectValue placeholder="Funding" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Funding</SelectItem>
            <SelectItem value="funded">Funded</SelectItem>
            <SelectItem value="unbudgeted">Unbudgeted</SelectItem>
            <SelectItem value="unresolved">Unresolved</SelectItem>
          </SelectContent>
        </Select>

        {activeFilters > 0 && (
          <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={clearFilters}>
            Clear filters ({activeFilters})
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {activeFilters > 0 && `${visible.length} of `}
          {inScope.length} {showAll ? 'request(s)' : 'pending request(s)'}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAll(v => !v)}>
            {showAll ? 'Pending only' : 'Show all'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setMsg(null); load(); }} disabled={loading}>
            <RefreshCw className={cn('h-4 w-4 mr-1', (loading || inFlight) && 'animate-spin')} /> Refresh
          </Button>
        </div>
      </div>

      {msg && (
        <div className={cn('rounded p-2 text-sm', msg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>
          {msg.text}
        </div>
      )}

      {loading && requests.length === 0 ? (
        <div className="py-12 text-center text-gray-400"><Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading…</div>
      ) : visible.length === 0 ? (
        <div className="py-12 text-center text-gray-400">
          <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
          {inScope.length > 0 ? (
            <>
              No {showAll ? '' : 'pending '}position requests match these filters.{' '}
              <button type="button" className="text-heritage-blue hover:underline" onClick={clearFilters}>
                Clear filters
              </button>
            </>
          ) : (
            <>No {showAll ? '' : 'pending '}position requests. Create one from the Jobs tab → ⋯ → Create Positions.</>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map(req => {
            const pc: any = req.proposed_changes || {};
            const groups: any[] | null = Array.isArray(pc.position_groups) ? pc.position_groups : null;
            const pr = pc.provision_result;
            const meta = STATUS_META[req.status] || { label: req.status, tone: 'text-gray-500 border-gray-200', icon: null };
            const isPending = req.status === 'pending_approval';
            // A provision runs as a server-side job; if it died (restart/crash) the request
            // sticks in 'approved' (Provisioning…) with no action. Offer a retry once it's
            // clearly stale (matches the backend's 15-min self-heal window) — re-approving
            // resumes it (retry-safe: only the missing positions are created).
            const isStuck = req.status === 'approved' && !!req.updated_at
              && (Date.now() - new Date(req.updated_at as string).getTime()) > 15 * 60 * 1000;
            const totalCount = groups ? groups.reduce((s, g) => s + (g.count || 1), 0) : (pc.count ?? pc.position_updates?.length);
            const jobLabel = pc.job_id
              ? (pc.title ? pc.title : pc.job_id)
              : (groups ? `${new Set(groups.map(g => g.job_id)).size} job(s)` : 'dimensions');
            return (
              <Card key={req.id}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{req.title}</span>
                        <Badge variant="outline" className={cn('text-[10px] gap-1', meta.tone)}>{meta.icon}{meta.label}</Badge>
                        <span className="text-[10px] text-gray-400 font-mono">{req.request_number}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3 w-3" />{jobLabel}
                          {pc.title && pc.job_id && <span className="font-mono text-gray-400">{pc.job_id}</span>}
                        </span>
                        <span>×{totalCount ?? '—'}</span>
                        {/* A hiring manager's appointment: an intern sits on a fixed-term position. */}
                        {pc.hire?.category && (
                          <span className="font-medium text-gray-700">{appointmentLabel(pc.hire.category)}</span>
                        )}
                        {pc.position_type_label && <span>{pc.position_type_label}</span>}
                        {pc.department_number && <span>Dept {pc.department_number}</span>}
                        {(pc.campus || pc.department || pc.faculty) && <span>{[pc.campus, pc.department, pc.faculty].filter(Boolean).join(' / ')}</span>}
                        {pc.start_date && <span>From {pc.start_date}{pc.end_date ? ` → ${pc.end_date}` : ''}</span>}
                        {pc.reports_to_position_id && <span>↳ {pc.reports_to_position_id}</span>}
                        {/* Budget linkage chip — the requests↔budget bridge */}
                        {(req as any).request_type !== 'update_position_dimensions' && !Array.isArray(pc.position_updates) && (
                          pc.funding?.mode === 'line' ? (() => {
                            // v2 stores lines[]; v1 stored one line's scalars. Read both,
                            // and NEVER fall back to a bare funding.annual for a multi-line
                            // ask — that is what rendered a funded request as "· · R0k".
                            const rows: any[] = Array.isArray(pc.funding.lines) ? pc.funding.lines
                              : (pc.funding.code || pc.funding.item_id ? [pc.funding] : []);
                            const codes = rows.map(l => l.code).filter(Boolean);
                            const total = rows.reduce((a, l) => a + Number(l.annual || 0), 0)
                              || Number(pc.funding.annual || 0);
                            const want = Number(pc.funding.positions || totalCount || rows.length || 1);
                            const partial = rows.length < want;
                            return (
                            <Badge variant="outline" className={cn('gap-1 text-[10px]', partial
                              ? 'border-amber-200 bg-amber-50 text-amber-800'
                              : 'border-emerald-200 bg-emerald-50 text-emerald-700')}
                              title={`Funded by RF1 line${codes.length > 1 ? 's' : ''} ${codes.join(', ') || '—'}`
                                + ` (${zarK(total)}/yr total across ${rows.length} line${rows.length === 1 ? '' : 's'})`
                                + (partial ? ` — ${want - rows.length} of ${want} position(s) still UNFUNDED` : '')
                                + (pc.funding.applied ? ' — applied' : ' — will link to the created seats')}>
                              <Wallet className="h-3 w-3" />
                              {partial ? `Funded ${rows.length} of ${want}` : 'Funded'}
                              {' · '}{codes.length > 1 ? `${codes.length} lines` : (codes[0] || '—')}
                              {' · '}{zarK(total)}
                            </Badge>
                            );
                          })() : pc.funding?.mode === 'unbudgeted' ? (() => {
                            // Per-position sign-offs: a 3-position ask with one signed off
                            // still has two unresolved, like "Funded 1 of 3" above. Only
                            // v2 carries lines[]; a v1 sign-off covered the whole ask.
                            const rows: any[] = Array.isArray(pc.funding.lines) ? pc.funding.lines : [];
                            const want = Number(pc.funding.positions || totalCount || rows.length || 1);
                            const partial = rows.length > 0 && rows.length < want;
                            return (
                            <Badge variant="outline" className={cn('gap-1 text-[10px]', partial
                              ? 'border-amber-200 bg-amber-50 text-amber-800'
                              : 'border-slate-200 bg-slate-50 text-slate-600')}
                              title={`Unbudgeted — signed off by ${pc.funding.signed_off_by}: “${pc.funding.reason}”`
                                + (partial ? ` — ${want - rows.length} of ${want} position(s) still unresolved` : '')}>
                              <Wallet className="h-3 w-3" />
                              {partial ? `Signed off ${rows.length} of ${want}` : 'Unbudgeted · signed off'}
                            </Badge>
                            );
                          })() : isPending ? (
                            // Only a request still waiting for a decision can be funded;
                            // a rejected or withdrawn one offered it too.
                            <button type="button"
                              className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 hover:bg-amber-100"
                              title="No funding resolved — approval is blocked until a budget line is attached or an unbudgeted sign-off is recorded."
                              onClick={() => openFunding(req, null)}>
                              <Wallet className="h-3 w-3" />
                              {groups && groups.length > 1 ? 'Resolve all' : 'Resolve funding'}
                            </button>
                          ) : null
                        )}
                        {(req.requested_by_name || req.requested_by_email) && (
                          <span
                            className="text-gray-400"
                            title={req.requested_by_email || undefined}
                          >
                            Requested by {req.requested_by_name || req.requested_by_email}
                          </span>
                        )}
                        {pc.origin && describeOrigin(pc.origin) && (
                          <span className="text-gray-500">· {describeOrigin(pc.origin)}</span>
                        )}
                        {isPending && (() => {
                          // Vacant positions of this job, type and dimensions — the
                          // reason to reject in favour of an existing seat.
                          const label = matchChipLabel(requestGroups(pc).map(
                            (_: unknown, gi: number) => seatChecks[`${req.id}:${gi}`]));
                          if (!label) return null;
                          return (
                            <button type="button"
                              onClick={() => setSeatsOpen(prev => {
                                const n = new Set(prev);
                                if (n.has(req.id)) n.delete(req.id); else n.add(req.id);
                                return n;
                              })}
                              className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-100"
                              title="Vacant positions that already match this request">
                              {label}
                            </button>
                          );
                        })()}
                      </div>
                      {pc.origin?.new_seat_reason && (
                        <div className="mt-1 text-[11px] text-gray-600">
                          <span className="font-medium">Why a new position:</span> {pc.origin.new_seat_reason}
                        </div>
                      )}
                      {/* A hiring manager's request comes with their business case. */}
                      {pc.origin?.channel === 'manager' && (
                        <BusinessCaseView className="mt-1" why={pc.hire?.business_case || req.business_justification}
                          approach={pc.hire?.selection_approach} campus={pc.campus || pc.hire?.campus} />
                      )}
                      {isPending && seatsOpen.has(req.id) && (
                        <div className="mt-2 space-y-2">
                          {requestGroups(pc).map((_: any, gi: number) => {
                            const r = seatChecks[`${req.id}:${gi}`];
                            return r && r.candidates.length ? (
                              <ExistingSeatsPanel key={gi} result={r} quietWhenEmpty />
                            ) : null;
                          })}
                        </div>
                      )}
                      {req.status === 'rejected' && pc.decision?.reason && (
                        <div className="mt-1 text-[11px] text-red-700">
                          Rejected{pc.decision.by_name ? ` by ${pc.decision.by_name}` : ''}: {pc.decision.reason}
                          {/* The picked position fills the reason ("Use vacant position X instead."),
                              so naming it again only when the reviewer wrote it out. */}
                          {pc.decision.use_instead_position_id
                            && !String(pc.decision.reason).includes(pc.decision.use_instead_position_id)
                            && <> · use <span className="font-mono">{pc.decision.use_instead_position_id}</span> instead</>}
                        </div>
                      )}
                      {req.status === 'cancelled' && pc.withdrawal && (
                        <div className="mt-1 text-[11px] text-gray-500">
                          Withdrawn{pc.withdrawal.by_name ? ` by ${pc.withdrawal.by_name}` : ''}{pc.withdrawal.reason ? `: ${pc.withdrawal.reason}` : ''}
                        </div>
                      )}
                      {groups && (() => {
                        const isOpen = groups.length <= 4 || expanded.has(req.id);
                        return (
                          <>
                            {groups.length > 4 && (
                              <button type="button"
                                onClick={() => setExpanded(prev => {
                                  const n = new Set(prev);
                                  if (n.has(req.id)) n.delete(req.id); else n.add(req.id);
                                  return n;
                                })}
                                className="mt-1 inline-flex items-center gap-1 text-[11px] text-heritage-blue hover:underline">
                                {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                {isOpen ? 'Hide' : 'Show'} {groups.length} group(s)
                              </button>
                            )}
                            {isOpen && (() => {
                              // Each job resolves on its own. Funding joins to a
                              // group by seat ordinal, the same address the
                              // backend uses, so no extra API call is needed.
                              const allSlots = askSlots(pc);
                              const fRows: any[] = Array.isArray(pc.funding?.lines) ? pc.funding.lines : [];
                              return (
                              <div className="mt-1 space-y-0.5 max-h-64 overflow-y-auto pr-1">
                                {groups.map((g: any, gi: number) => {
                                  const ords = allSlots.filter(sl => sl.groupIndex === gi).map(sl => sl.ordinal);
                                  const mine = fRows.filter(l => ords.includes(Number(l.seat_ordinal)));
                                  const cash = mine.filter(l => l.kind !== 'unbudgeted');
                                  const done = ords.length > 0 && mine.length >= ords.length;
                                  const total = cash.reduce((a, l) => a + Number(l.annual || 0), 0);
                                  return (
                                  <div key={gi} className="text-[11px] text-gray-500 flex flex-wrap items-center gap-x-2">
                                    <span className="text-gray-700">{g.title || g.job_id}</span>
                                    {g.title && g.job_id && <span className="font-mono text-gray-400">{g.job_id}</span>}
                                    <span>{[g.campus, g.department, g.faculty].filter(Boolean).join(' / ') || '—'}</span>
                                    {(g.position_type_label || g.position_type_id) && <span>· {g.position_type_label || g.position_type_id}</span>}
                                    <span>· ×{g.count || 1}</span>
                                    {g.reports_to_position_id && <span>· ↳ {g.reports_to_position_id}</span>}
                                    {isPending && (
                                      <button type="button" onClick={() => openFunding(req, gi)}
                                        className={cn('inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
                                          !done ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
                                            : cash.length ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                              : 'border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200')}
                                        title={done
                                          ? (cash.length
                                            ? `Funded by ${cash.map(l => l.code).join(', ')} — click to change`
                                            : `Unbudgeted sign-off: “${mine[0]?.reason || ''}” — click to change`)
                                          : `${ords.length - mine.length} of ${ords.length} position(s) here are unresolved — attach a budget line or sign off`}>
                                        <Wallet className="h-3 w-3" />
                                        {!done
                                          ? (mine.length ? `Resolve ${ords.length - mine.length} more` : 'Resolve funding')
                                          : cash.length
                                            ? `${cash.length > 1 ? `${cash.length} lines` : cash[0].code} · ${zarK(total)}`
                                            : 'Unbudgeted'}
                                      </button>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                              );
                            })()}
                          </>
                        );
                      })()}
                      {pr && (
                        <div className="text-[11px] mt-1">
                          <span className="text-emerald-600">{(pr.updated ?? pr.created) ?? 0} {pr.updated != null ? 'updated' : 'created'}</span>
                          {pr.failed > 0 && <span className="text-amber-600"> · {pr.failed} still to {pr.updated != null ? 'update' : 'provision'}</span>}
                          {Array.isArray(pr.position_ids) && pr.position_ids.length > 0 && (
                            <span className="text-gray-400 font-mono"> · {pr.position_ids.slice(0, 12).join(', ')}{pr.position_ids.length > 12 ? `… (+${pr.position_ids.length - 12})` : ''}</span>
                          )}
                        </div>
                      )}
                      {pc.provision_error && (
                        <div className="text-[11px] text-red-600 mt-1">⚠ {pc.provision_error}</div>
                      )}
                    </div>
                    {(isPending || isStuck) && (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => act(req, 'approve')} disabled={acting === req.id}
                          title={isStuck ? 'This provision looks stuck (its background job likely died) — retry to resume it.' : undefined}>
                          {acting === req.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5 mr-1" />}
                          {isStuck ? 'Retry (stuck)' : pr && pr.failed > 0 ? 'Provision rest' : 'Approve'}
                        </Button>
                        {isPending && (
                          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => openReject(req)} disabled={acting === req.id}>
                            Reject
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Funding-resolution dialog — the budget↔requests bridge */}
      <Dialog open={!!fundingFor} onOpenChange={o => { if (!o) { setFundingFor(null); setFundingGroup(null); } }}>
        {/* The zero-minimum column now lives on the shared primitive. What is
            still local: grid-rows + overflow-hidden move the scroll to the body
            so the Attach button stops scrolling out of reach — that one is NOT
            safe to apply globally, since it would stretch a short dialog. */}
        <DialogContent className="max-w-xl grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-sm">
              Resolve funding · {fundingGroupLabel || fundingFor?.title}
            </DialogTitle>
          </DialogHeader>
          {resolving ? (
            <div className="py-8 text-center text-gray-400"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />Checking the budget…</div>
          ) : resolution && (
            <div className="min-h-0 space-y-3 overflow-y-auto pr-1 text-xs">
              {resolution.seats.length > 0 && (
                <div className="rounded border border-sky-200 bg-sky-50 p-2 break-words">
                  <div className="mb-1 font-semibold text-sky-800">
                    {resolution.seats.some(x => x.dims_match)
                      ? <>Funded vacant seat{resolution.seats.length > 1 ? 's' : ''} already exist{resolution.seats.length > 1 ? '' : 's'} for this role:</>
                      : <>Funded vacant seats exist for this role, but none at these dimensions:</>}
                  </div>
                  {resolution.seats.slice(0, 4).map(s => (
                    <div key={s.fo_position_id} className="text-sky-900">
                      <span className="font-mono text-[10px]">{s.fo_position_id}</span> · {s.title} ·{' '}
                      {dimLabel(s)}
                      {s.employment_type && <span className="text-sky-700"> · {s.employment_type}</span>}
                      {' · '}{zarK(s.budgeted_annual)}
                      {dimTag(s) && (
                        <span className={cn(s.match_tier === 2 ? 'font-medium text-amber-700' : 'text-sky-600')}> ({dimTag(s)})</span>
                      )}
                      {/* Reusing a seat inherits ITS manager — the reviewer must see this. */}
                      {s.reports_to ? (
                        <span className={cn('ml-1', s.reports_to_match === false ? 'text-amber-700' : 'text-sky-600')}
                          title={s.reports_to_match === false
                            ? `This seat reports to ${s.reports_to}; the request asked for ${resolution.asked_reports_to}.`
                            : 'Reporting line matches the request.'}>
                          · ↳ {s.reports_to}{s.reports_to_manager ? ` ${s.reports_to_manager}` : ''}
                          {s.reports_to_match === false ? ' ⚠ differs' : s.reports_to_match ? ' ✓' : ''}
                        </span>
                      ) : (
                        <span className="ml-1 text-amber-700" title="This seat has no reporting line set.">· ↳ none</span>
                      )}
                      {/* Repair it in place — writes the F&O PositionHierarchies
                          'Line' edge (position-anchored, valid on a vacant seat). */}
                      {resolution.asked_reports_to && s.reports_to_match === false && s.dims_match && (
                        <button type="button" disabled={rtBusy === s.fo_position_id}
                          className="ml-1.5 rounded border border-sky-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-sky-800 hover:bg-sky-100 disabled:opacity-50"
                          title={`Re-point ${s.fo_position_id} at ${resolution.asked_reports_to} in F&O now`}
                          onClick={() => fixReportingLine(s.fo_position_id, resolution.asked_reports_to!)}>
                          {rtBusy === s.fo_position_id
                            ? <Loader2 className="inline h-3 w-3 animate-spin" />
                            : <>Repoint to {resolution.asked_reports_to}</>}
                        </button>
                      )}
                    </div>
                  ))}
                  {(resolution.totals?.seats ?? resolution.seats.length) > 4 && (
                    <div className="text-[10px] text-sky-600">
                      +{(resolution.totals?.seats ?? resolution.seats.length) - 4} more, ranked lower
                    </div>
                  )}
                  {resolution.seats.some(x => x.dims_match) ? (
                    <div className="mt-1 text-[11px] text-sky-700">
                      Consider <b>rejecting</b> this request and hiring onto one of the matching seats instead.
                      Creating another seat duplicates funded capacity: two seats, one budget, and the
                      empty one can&apos;t be retired while it still holds money.
                      {resolution.asked_reports_to && (
                        <> Reusing a seat keeps <b>that seat&apos;s</b> reporting line — a ⚠ means it differs
                        from the requested manager ({resolution.asked_reports_to}); <b>Repoint</b> fixes it
                        in F&amp;O immediately.</>
                      )}
                    </div>
                  ) : (
                    <div className="mt-1 text-[11px] text-sky-700">
                      None of these matches the request&apos;s campus, department
                      {resolution.resolved_dims?.faculty ? ' and faculty' : ''}, so reusing one would put
                      this hire on a seat budgeted elsewhere. Attaching a budget line below is the usual route.
                    </div>
                  )}
                </div>
              )}
              {resolution.resolved_dims?.faculty_ignored && (
                <div className="text-[11px] text-amber-700">
                  This request&apos;s faculty isn&apos;t a recognised faculty code, so candidates were
                  matched on campus and department only.
                </div>
              )}
              {resolution.ask_type ? (
                <div className="text-[11px] text-gray-500">
                  This ask is <b>{resolution.ask_type}</b> — budget is held per position type, so only
                  {' '}{resolution.ask_type} seats and lines are offered
                  {(resolution.blocked_by_type?.seats || resolution.blocked_by_type?.lines) ? (
                    <span className="text-amber-700">
                      {' '}({resolution.blocked_by_type?.seats || 0} seat(s) and{' '}
                      {resolution.blocked_by_type?.lines || 0} line(s) of the same role hidden — wrong type)
                    </span>
                  ) : ' (none of another type exist for this role)'}.
                </div>
              ) : (
                <div className="text-[11px] text-amber-700">
                  ⚠ This request doesn&apos;t specify a position type, so candidates are <b>not</b> type-filtered —
                  budget is held per type, so check the type on each option before attaching.
                </div>
              )}
              {(() => {
                const pc: any = fundingFor?.proposed_changes || {};
                const slots = askSlots(pc);
                const allGroups = Array.from(new Set(slots.map(sl => sl.groupIndex)));
                const multiJob = allGroups.length > 1;
                const groupIdx = allGroups.filter(gi => fundingGroup == null || gi === fundingGroup);
                const takenBy = (ord: number) => picks[ord];
                const toggle = (gi: number, itemId: number) => {
                  setPicks(prev => {
                    const next = { ...prev };
                    const mine = slots.filter(sl => sl.groupIndex === gi).map(sl => sl.ordinal);
                    const held = mine.find(o => next[o] === itemId);
                    if (held !== undefined) { delete next[held]; return next; }
                    if (Object.values(next).includes(itemId)) return prev;  // one line, one seat
                    const free = mine.find(o => next[o] === undefined);
                    if (free === undefined) return prev;                    // group already covered
                    next[free] = itemId;
                    return next;
                  });
                  setUnbudgetedReason('');
                };
                return groupIdx.map(gi => {
                  const g = slots.find(sl => sl.groupIndex === gi)?.group || {};
                  const mine = slots.filter(sl => sl.groupIndex === gi);
                  const res = resolutions[gi];
                  const chosen = mine.filter(sl => takenBy(sl.ordinal) !== undefined).length;
                  return (
                    <div key={gi}>
                      <div className="mb-1 flex items-baseline gap-2 font-semibold text-gray-700">
                        <span>
                          Attach {mine.length > 1 ? `${mine.length} budget lines` : 'a budget line'}
                          {multiJob && <> for <span className="font-mono text-[10px]">{g.job_id}</span> {g.title}</>}
                        </span>
                        <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium',
                          chosen === mine.length ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800')}>
                          {chosen} of {mine.length} funded
                        </span>
                        {multiJob && (
                          <span className="text-[10px] font-normal text-gray-500">
                            {dimLabel(g)}
                          </span>
                        )}
                      </div>
                      {!res ? <div className="py-2 text-gray-400">Loading candidates…</div>
                        : res.lines.length ? res.lines.map(l => {
                          const on = Object.values(picks).includes(l.item_id);
                          const blockedType = !!l.type_conflict && !fundingOverride;
                          const full = chosen >= mine.length && !on;
                          return (
                            <label key={l.item_id} className={cn('flex items-center gap-2 rounded border-b px-1 py-1.5 last:border-0',
                              blockedType || full ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-muted/40')}>
                              <input type="checkbox" checked={on} disabled={blockedType || full}
                                onChange={() => toggle(gi, l.item_id)} />
                              <span className="font-mono text-[10px] text-gray-600">{l.code}</span>
                              <span className="min-w-0 flex-1 truncate">
                                {l.title} · {dimLabel(l)}
                                {l.employment_type && <span className="text-gray-500"> · {l.employment_type}</span>}
                                {l.on_vacant_seat && (
                                  <span className="ml-1 text-[10px] text-sky-700"
                                    title={`This money currently sits on vacant seat ${l.on_vacant_seat} — attaching MOVES it to the new seat when the request is provisioned.`}>
                                    · moves off {l.on_vacant_seat}
                                  </span>
                                )}
                              </span>
                              <span className="tabular-nums">{zarK(l.annual)}</span>
                              {dimTag(l) && (
                                <span className={cn('text-[10px]', l.match_tier === 2 ? 'font-medium text-amber-700' : 'text-amber-600')}>{dimTag(l)}</span>
                              )}
                              {l.type_conflict && <span className="text-[10px] font-medium text-rose-600">{l.employment_type} budget</span>}
                            </label>
                          );
                        }) : <div className="py-2 text-gray-400">No matching funded lines available (unallocated, or parked on a vacant seat of this role).</div>}
                      {res && (res.totals?.lines ?? 0) > res.lines.length && (
                        <div className="mt-1 text-[10px] text-gray-500">
                          +{(res.totals?.lines ?? 0) - res.lines.length} more line(s) of this role, ranked lower
                        </div>
                      )}
                      {chosen > 0 && chosen < mine.length && (
                        <div className="mt-1 text-[11px] text-amber-700">
                          ⚠ {mine.length - chosen} of these {mine.length} position(s) will be created
                          <b> unfunded</b>. Attach more lines, or approve knowing the balance is unbudgeted.
                        </div>
                      )}
                      {res?.lines.some(l => l.type_conflict) && (
                        <label className="mt-1 flex cursor-pointer items-center gap-1.5 text-[11px] text-gray-600">
                          <input type="checkbox" checked={fundingOverride}
                            onChange={e => { setFundingOverride(e.target.checked); if (!e.target.checked) setPicks({}); }} />
                          Allow a different position type (deliberate override — e.g. FTC hires against PERM-only
                          budget with the hiring manager&apos;s agreement; recorded on the request and the ledger)
                        </label>
                      )}
                    </div>
                  );
                });
              })()}
              <div>
                <div className="mb-1 font-semibold text-gray-700">
                  …or record an <span className="text-rose-700">unbudgeted</span> sign-off
                  {fundingGroup != null ? ' for this job' : ' for the unfunded balance'} (goes on the audit ledger):
                </div>
                <Input className="h-8 text-xs" value={unbudgetedReason}
                  onChange={e => { setUnbudgetedReason(e.target.value); if (e.target.value) setPicks({}); }}
                  placeholder="e.g. Growth hire approved by CFO outside RF1 — fund in 2027 baseline" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" disabled={fundingBusy}
              onClick={() => { setFundingFor(null); setFundingGroup(null); }}>Cancel</Button>
            <Button size="sm" onClick={saveFunding}
              disabled={fundingBusy || resolving
                || (!fundingPickCount && unbudgetedReason.trim().length < 5 && !fundingWasResolved)}>
              {fundingBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wallet className="mr-1 h-3.5 w-3.5" />}
              {fundingPickCount
                ? `Attach ${fundingPickCount} line${fundingPickCount > 1 ? 's' : ''}`
                : (unbudgetedReason.trim().length >= 5 ? 'Sign off unbudgeted' : 'Clear funding')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject — with the reason the requester reads in HR Ops, and the
          existing position to use instead when that is the reason. */}
      <Dialog open={!!rejecting} onOpenChange={o => { if (!o) setRejecting(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm">Reject · {rejecting?.title}</DialogTitle>
          </DialogHeader>
          {rejecting && (() => {
            const seats = seatsFor(rejecting);
            return (
              <div className="space-y-3 text-xs">
                <p className="text-gray-600">
                  {rejecting.requested_by_name || 'The person who raised it'} sees this in HR Ops → Position
                  requests.
                </p>
                {seats.length > 0 && (
                  <div>
                    <label className="mb-1 block font-medium text-gray-700" htmlFor="reject-use-instead">
                      Use an existing vacant position instead
                    </label>
                    <select id="reject-use-instead"
                      className="h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs"
                      value={useInstead}
                      onChange={e => {
                        const v = e.target.value;
                        setUseInstead(v);
                        if (v && !rejectReason.trim()) setRejectReason(`Use vacant position ${v} instead.`);
                      }}>
                      <option value="">No — reject without naming one</option>
                      {seats.map(s => (
                        <option key={s.fo_position_id} value={s.fo_position_id}>
                          {s.fo_position_id} · {s.title} · {dimsLabel(s) || 'no dimensions'}
                          {s.reports_to_match === false ? ' · other manager' : ''}
                          {s.funded ? ' · budget linked' : ''}
                          {s.strength === 'elsewhere' ? ' · other campus/department' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="mb-1 block font-medium text-gray-700" htmlFor="reject-reason">
                    Reason <span className="text-red-600">*</span>
                  </label>
                  <textarea id="reject-reason"
                    className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs"
                    rows={3} maxLength={1000}
                    placeholder="e.g. Use vacant position 002373 instead; it has budget."
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)} />
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRejecting(null)}
              disabled={!!rejecting && acting === rejecting.id}>Cancel</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={confirmReject}
              disabled={rejectReason.trim().length < 5 || (!!rejecting && acting === rejecting.id)}>
              {rejecting && acting === rejecting.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Reject request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PositionRequestsTab;
